import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { formatPhone } from "@/lib/utils";
import { nameIssue, phoneIssue } from "@/lib/profileValidation";
import {
  BadgeCheck,
  Camera,
  Check,
  CheckCircle2,
  DollarSign,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Sparkles,
  Star,
  X,
} from "lucide-react";

import { api, assetUrl } from "@/lib/api";
import { buyerStatusLabel, describeBuyerProfile } from "@/lib/buyerProfile";
import type { UserLocation } from "@/lib/userLocation";
import BuyBoxPanel from "@/components/BuyBoxPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { User } from "@/types";

interface CategoryItem {
  id: number;
  name: string;
}

interface Props {
  /** Industry categories already loaded by the parent (id → name resolution). */
  categories: CategoryItem[];
  /** Opens the multi-step buyer-preferences editor. */
  onEdit: () => void;
}

/* Read-only field box styled to mirror the reference inputs.
   min-w-0 + break-words keep long values (many industries, long locations)
   wrapping inside the column instead of stretching the grid past the card. */
function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-bold tracking-wide text-muted-foreground">{label}</div>
      <div
        className="mt-1.5 break-words rounded-lg border border-border/80 bg-background/50 px-3.5 py-2.5 text-sm font-semibold leading-relaxed text-foreground"
      >
        {value || <span className="font-normal italic text-muted-foreground/60">Not set</span>}
      </div>
    </div>
  );
}

/* A single labelled strength meter. */
function StrengthBar({ label, pct, hint }: { label: string; pct: number; hint?: string | null }) {
  const barColor =
    pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-primary" : pct > 0 ? "bg-amber-500" : "bg-rose-400";
  const textColor =
    pct >= 80 ? "text-emerald-600" : pct >= 50 ? "text-primary" : pct > 0 ? "text-amber-600" : "text-rose-500";
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-muted-foreground">{label}</span>
        <span className={`font-bold ${textColor}`}>{pct}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {hint && <div className="mt-1 text-[0.68rem] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function BuyerProfileCard({ categories, onEdit }: Props) {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const prefs = user?.buyer_preferences ?? null;

  // Tier name comes from the session (plan-name gating convention); only the
  // price needs a fetch, and only when there is a paid plan to price.
  const planName = user?.plan_name ?? null;
  const [planAmount, setPlanAmount] = useState<number | null>(null);
  const [introsCount, setIntrosCount] = useState<number | null>(null);

  // Inline personal-details editing + avatar upload
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [saving, setSaving] = useState(false);
  // Home state (TKT-00157): optional; staff use it for the buyer's timezone.
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [usStates, setUsStates] = useState<{ id: number; name: string }[]>([]);
  const [homeStateId, setHomeStateId] = useState("");

  useEffect(() => {
    api.get<UserLocation>("/profile/me/location").then(setLocation).catch(() => {});
  }, []);

  // States are only needed once the edit form opens.
  useEffect(() => {
    if (!editing || usStates.length) return;
    api
      .get<{ id: number; name: string }[]>("/lookups/countries")
      .then((countries) => {
        const us = countries.find((c) => c.name === "United States");
        return api.get<{ id: number; name: string }[]>(`/lookups/states${us ? `?country_id=${us.id}` : ""}`);
      })
      .then(setUsStates)
      .catch(() => {});
  }, [editing, usStates.length]);

  const startEdit = () => {
    setFirstName(user?.first_name ?? "");
    setLastName(user?.last_name ?? "");
    setPhone(user?.phone ?? "");
    setHomeStateId(location?.home_state_id != null ? String(location.home_state_id) : "");
    setEditing(true);
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 2 MB.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setAvatarLoading(true);
    try {
      const updated = await api.upload<User>("/profile/me/avatar", file);
      updateUser(updated);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Photo upload failed");
    } finally {
      setAvatarLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Live validation for the edit form — mirrors the backend rules.
  const firstError = editing ? nameIssue(firstName, "First name") : null;
  const lastError = editing ? nameIssue(lastName, "Last name") : null;
  const phoneError = editing ? phoneIssue(phone) : null;
  const detailsInvalid = !!(firstError || lastError || phoneError);

  const handleSaveDetails = async (event: FormEvent) => {
    event.preventDefault();
    const issue = firstError ?? lastError ?? phoneError;
    if (issue) {
      toast.error(issue);
      return;
    }
    setSaving(true);
    try {
      const updated = await api.patch<User>("/profile/me", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
      });
      updateUser(updated);
      const nextState = homeStateId ? Number(homeStateId) : null;
      if (nextState !== (location?.home_state_id ?? null)) {
        setLocation(await api.put<UserLocation>("/profile/me/location", { home_state_id: nextState }));
      }
      setEditing(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!planName) {
      setPlanAmount(null);
      return;
    }
    api
      .get<{ amount_per_month: number }>("/payments/current-tier")
      .then((t) => setPlanAmount(t.amount_per_month))
      .catch(() => {});
  }, [planName]);

  useEffect(() => {
    api
      .get<{ total_elements: number }>("/interests/mine?page=1&page_size=1")
      .then((r) => setIntrosCount(r.total_elements))
      .catch(() => {});
  }, []);

  /* ── Derived display values (shared with the staff BuyerProfileModal) ── */
  const view = useMemo(
    () =>
      describeBuyerProfile(
        {
          ...(prefs ?? {
            experience: null, budget_min: null, budget_max: null, category_ids: [], other_category: null,
            years_in_business: null, funding_methods: [], additional_notes: null, preferred_location: null,
            sde_preference: null,
          }),
          phone: user?.phone ?? null,
          avatar_url: user?.avatar_url ?? null,
        },
        categories,
      ),
    [prefs, categories, user?.phone, user?.avatar_url],
  );

  if (!user) return null;

  const initials =
    `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() || "U";
  const memberSince = new Date(user.created_at).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });

  const { experience, funding, prequal } = view;

  const hasPrefs = Boolean(prefs);

  return (
    <div className="space-y-5">
      {/* ── Header row ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground">My Profile</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A strong profile gets faster concierge responses from sellers
          </p>
        </div>
        <Button onClick={onEdit} className="rounded-xl shrink-0">
          <Pencil className="h-4 w-4" />
          Edit Buy Box
        </Button>
      </div>

      {/* The Buy Box leads (wide column, top on mobile); the identity +
          strength sidebar sits on the left on desktop via lg:order-first —
          same layout as the staff BuyerProfileModal.
          minmax(0,1fr): without the 0 floor, long BuyBox values push the
          column wider than the page and the whole panel scrolls sideways. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* ══ Main: Buy Box + financial qualification ══ */}
        {hasPrefs ? (
          <div className="min-w-0 space-y-5">
            <BuyBoxPanel
              view={view}
              subtitle="The business you're looking to acquire — sellers and advisors match you on this"
              sdeConflict={
                <>
                  Your minimum SDE is higher than your max budget, so almost no listing can match.{" "}
                  <button type="button" onClick={onEdit} className="font-bold underline">Fix it</button>
                </>
              }
            />

            {/* Financial Qualification */}
            <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
                  <DollarSign className="h-4 w-4" />
                </div>
                <h3 className="text-lg font-bold text-foreground">Financial Qualification</h3>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Buying Experience" value={experience} />
                <Field label="Financing Preferences" value={funding.join(", ") || null} />
                <Field label="Pre-Qualified" value={prequal.join(", ") || null} />
              </div>

              {prefs?.additional_notes && (
                <div className="mt-4">
                  <div className="text-xs font-bold tracking-wide text-muted-foreground">Additional Notes</div>
                  <p className="mt-1.5 break-words rounded-lg border border-border/80 bg-background/50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
                    {prefs.additional_notes}
                  </p>
                </div>
              )}

              <div className="mt-5 flex items-center gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-600" />
                <p className="text-sm font-medium text-emerald-700">
                  Buy Box on file — sellers can see your acquisition criteria and reach out faster.
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Empty state — no preferences set yet */
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border bg-muted/20 px-6 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles className="h-7 w-7" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">Build your Buy Box</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Tell sellers what you're looking for — industries, geography, budget, and how you'll finance the deal.
              </p>
            </div>
            <Button onClick={onEdit} className="rounded-xl">
              <CheckCircle2 className="h-4 w-4" />
              Set up my profile
            </Button>
          </div>
        )}
        {/* ══ Sidebar: identity + strength ══ */}
        <div className="self-start overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm lg:order-first">
          {/* Banner */}
          <div className="relative h-24 bg-[linear-gradient(135deg,hsl(41_58%_44%),hsl(38_52%_28%))]">
            <div className="group absolute -bottom-9 left-5 h-20 w-20 overflow-hidden rounded-full border-4 border-card bg-primary shadow-md">
              {user.avatar_url ? (
                <img src={assetUrl(user.avatar_url)!} alt="Avatar" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-2xl font-black text-primary-foreground">
                  {initials}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={avatarLoading}
                className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                aria-label="Upload photo"
              >
                {avatarLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                ) : (
                  <Camera className="h-5 w-5" />
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
          </div>

          <div className="px-5 pb-5 pt-12">
            {editing ? (
              <form onSubmit={handleSaveDetails} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="bpFirst" className="text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">First</Label>
                    <Input id="bpFirst" value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={60} className="h-9 text-sm" />
                    {firstName.trim() !== "" && firstError && (
                      <p className="text-[0.68rem] font-medium text-destructive">{firstError}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="bpLast" className="text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">Last</Label>
                    <Input id="bpLast" value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={60} className="h-9 text-sm" />
                    {lastName.trim() !== "" && lastError && (
                      <p className="text-[0.68rem] font-medium text-destructive">{lastError}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bpPhone" className="text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">Phone</Label>
                  <Input id="bpPhone" type="tel" placeholder="+1 (555) 000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} className="h-9 text-sm" />
                  {phoneError && (
                    <p className="text-[0.68rem] font-medium text-destructive">{phoneError}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bpHomeState" className="text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">Home state</Label>
                  <select
                    id="bpHomeState"
                    value={homeStateId}
                    onChange={(e) => setHomeStateId(e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                  >
                    <option value="">Not set</option>
                    {usStates.map((st) => (
                      <option key={st.id} value={st.id}>{st.name}</option>
                    ))}
                  </select>
                  <p className="text-[0.68rem] text-muted-foreground">Helps your advisor call at a good time.</p>
                </div>
                <div className="flex gap-2 pt-0.5">
                  <Button type="submit" size="sm" disabled={saving || detailsInvalid} className="rounded-lg">
                    <Check className="h-3.5 w-3.5" />
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => setEditing(false)}>
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-lg font-extrabold tracking-tight text-foreground">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="mt-0.5 text-sm font-medium text-muted-foreground">{buyerStatusLabel(user.status)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={startEdit}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
                    title="Edit details"
                    aria-label="Edit details"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Mail className="mt-[3px] h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 break-all">{user.email}</span>
                    {user.email_verified ? (
                      <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500" aria-label="Email verified" />
                    ) : (
                      <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase text-amber-600">
                        Unverified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    <span>{formatPhone(user.phone) || "Not provided"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    {location?.home_state ? (
                      <span>{location.home_state}</span>
                    ) : (
                      <button type="button" onClick={startEdit} className="text-left hover:text-primary hover:underline">
                        Add your home state
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Plan badge */}
            {planName ? (
              <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-primary/25 bg-primary/10 px-3.5 py-3">
                <Star className="h-4 w-4 shrink-0 fill-primary text-primary" />
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wide text-primary">{planName} Plan</div>
                  {planAmount != null && (
                    <div className="text-[0.7rem] text-primary/80">
                      ${planAmount.toLocaleString()}/month
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* No paid tier = the free Basic plan, same flat style the staff
                 modal uses for it. */
              <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-border/70 bg-muted/30 px-3.5 py-3">
                <Star className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Basic Plan</div>
                  <div className="text-[0.7rem] text-muted-foreground/80">Free — no paid subscription</div>
                </div>
              </div>
            )}

            {/* Quick stats */}
            <div className="mt-5 space-y-3 border-t border-border/70 pt-4">
              {[
                { label: "Intros Requested", value: introsCount ?? "—" },
                { label: "Member Since", value: memberSince },
              ].map((s) => (
                <div key={s.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{s.label}</span>
                  <span className="font-bold text-foreground">{s.value}</span>
                </div>
              ))}
            </div>

            {/* Profile strength */}
            <div className="mt-5 rounded-xl border border-border/70 bg-muted/30 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-foreground">Profile Strength</span>
                <span className="text-sm font-extrabold text-primary">{view.overallPct}%</span>
              </div>
              <div className="space-y-3">
                {view.strength.map((s) => (
                  <StrengthBar key={s.label} label={s.label} pct={s.pct} hint={s.hint} />
                ))}
              </div>
              {view.nextStep && (
                <div className="mt-3 border-t border-border/70 pt-3 text-xs text-muted-foreground">
                  <span className="font-bold text-foreground">Next step:</span> {view.nextStep}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
